import { Honeybadger } from '@honeybadger-io/react';

import {
  LEADGEN_BACKEND_API_KEY,
  LEADGEN_BACKEND_SAVE_DATA_ENDPOINT,
  AI_CONTENT_FROM_TOPIC_ENDPOINT,
  AI_CONTENT_FROM_YOUTUBE_ENDPOINT,
  KAJABI_PRODUCTS_API_KEY,
} from '../utils/lead-gen-constants';
import {
  StepUpdateType,
  VariationDataType,
} from './types/lead-gen-service-types';

class LeadGenService {
  variationData: VariationDataType;

  /**
   * Initializes the service class with the given variation data from the current
   * lead gen experience configuration, to be used for the duration of the experience
   * @param initializeData
   */
  constructor(initializeData: VariationDataType) {
    this.variationData = initializeData;
  }

  /**
   * Store the most recent data for currentStep in localStorage to provide a means for
   * keeping the user's place in the event they leave the wizard incomplete, and also
   * send the stepProgressData to braze via the backend.
   * This should be called immediately when a new slide is rendered.
   * @param stepProgressData
   */
  static updateStepProgressData = (stepProgressData: StepUpdateType) => {
    // with this data, we can save to localStorage the metadata required
    // to resume the current position given a specific lead gen experience config.
    localStorage.setItem('lg-current-step', JSON.stringify(stepProgressData));
  };

  /**
   * @returns the parsed current step data found in local storage, or null
   */
  static retrieveStepData = () => {
    const currentStep = localStorage.getItem('lg-current-step');
    if (currentStep) {
      return JSON.parse(currentStep);
    }
  };

  /**
   * Post submitted step data to the backend API
   * @param stepData
   * @returns a promise
   */
  static submitStepData = async (stepData: object) => {
    try {
      const response = await fetch(LEADGEN_BACKEND_SAVE_DATA_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'x-api-key': LEADGEN_BACKEND_API_KEY,
        },
        body: JSON.stringify(stepData),
      });
      if (response.status === 200) {
        return { success: true };
      } else {
        return {
          success: false,
          message: 'Something went wrong, please try again later',
        };
      }
    } catch (e: any) {
      Honeybadger.notify(e);

      return {
        success: false,
        message: 'Something went wrong, please try again later',
      };
    }
  };

  /**
   * Submits aiData (from userData) to CopyMagic and returns AI-generated
   * content.
   * @param aiData
   * @returns a promise
   */
  static createAiData = async (aiData: any) => {
    try {
      const endpoint = aiData.youtube_url
        ? AI_CONTENT_FROM_YOUTUBE_ENDPOINT
        : AI_CONTENT_FROM_TOPIC_ENDPOINT;

      // @ts-ignore
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'x-api-key': KAJABI_PRODUCTS_API_KEY,
        },
        body: JSON.stringify(aiData),
      });

      if (response.status === 200) {
        const data = await response.json();
        return { success: true, data: data.content };
      } else {
        return {
          success: false,
          message: 'Something went wrong, please try again later',
        };
      }
    } catch (e: any) {
      Honeybadger.notify(e);

      return {
        success: false,
        message: 'Something went wrong, please try again later',
      };
    }
  };
}

export default LeadGenService;
